import { UserAction, NestedHome, NestedProject, NestedSearch, SellerActionData } from '@habity/types';
import { postAPI } from '~/lib/api';
import { API_PATHS } from '@habity/constants';

export interface ExecuteUserActionParams {
  /** User's email */
  email: string;
  /** Agency ID for tracking */
  agencyId?: number;
  /** The home to perform the action on */
  home?: NestedHome;
  /** The project to perform the action on */
  project?: NestedProject;
  /** The action to perform */
  action: UserAction;
  /** Optional viewing slot ID for booking viewings */
  viewingSlotId?: number;
  /** Optional search filters for created_search_profile action */
  searchFilters?: Partial<NestedSearch>;
  /** Optional seller action data */
  sellerActionData?: SellerActionData;
  /** Optional additional user data (for new user signup) */
  userData?: Record<string, unknown>;
}

export interface ExecuteUserActionResult {
  success: boolean;
  error?: string;
  user?: any;
  /** Full response from the API for additional data (e.g., home, valuation) */
  response?: any;
}

/**
 * Execute a user action (buyer or seller) via the backend API.
 * 
 * This is a pure function that can be called from:
 * - useUserAction hook (for logged-in users with complete profiles)
 * - TwoStepEnhancedLoginForm (after login/signup completes)
 * 
 * It handles:
 * - Buyer actions (wishlist, interest, viewing, etc.)
 * - Seller actions (claimed_home, requested_broker_valuation, etc.)
 * - Project actions (project interest)
 */
export async function executeUserAction({
  email,
  agencyId,
  home,
  project,
  action,
  viewingSlotId,
  searchFilters,
  sellerActionData,
  userData,
}: ExecuteUserActionParams): Promise<ExecuteUserActionResult> {
  try {
    // Build base request data
    const requestData: Record<string, unknown> = {
      email,
      userData: {
        email,
        agency_id: agencyId,
        ...userData,
      },
      action,
    };

    // Add home-specific data
    if (home) {
      requestData.homeId = home.id;
    }

    // Add viewing slot for booked_viewing action
    if (action === 'booked_viewing' && viewingSlotId) {
      requestData.viewingSlotId = viewingSlotId;
    }

    // Add search filters for created_search_profile action
    if (action === 'created_search_profile') {
      requestData.searchFilters = searchFilters || {};
      (requestData.userData as Record<string, unknown>).app_mode = 'buyer';
    }

    // Add seller action data for seller events
    if (sellerActionData) {
      requestData.sellerActionData = sellerActionData;
    }

    // Handle project actions separately (different endpoint)
    if (project && !home) {
      // First create/update the user
      const userResult = await postAPI(
        '/providers/supabase/users',
        JSON.stringify({
          email,
          agency_id: agencyId,
        })
      );

      if (userResult.error) {
        return {
          success: false,
          error: userResult.message || 'Ett fel uppstod',
        };
      }

      // Then create the project interest
      const interestResult = await postAPI(
        '/providers/supabase/project-interests',
        JSON.stringify({
          project_id: project.id,
          user_id: userResult.userId,
          interest_level: action,
          user_event: action,
          origin: 'internal',
        })
      );

      if (interestResult.error) {
        return {
          success: false,
          error: interestResult.message || 'Ett fel uppstod',
        };
      }

      return {
        success: true,
        user: userResult.user,
        response: interestResult,
      };
    }

    // All other actions go through the unified users/action endpoint
    const result = await postAPI(
      API_PATHS.PROVIDERS.SUPABASE.USERS.ACTION,
      JSON.stringify(requestData)
    );

    if (result.error) {
      return {
        success: false,
        error: result.message || 'Ett fel uppstod',
      };
    }

    return {
      success: true,
      user: result.user,
      response: result,
    };
  } catch (error) {
    console.error('Error executing user action:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Ett fel uppstod',
    };
  }
}
